'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { useLocale } from 'next-intl';
import { useEvent } from 'react-use';

import { useSetTranslations } from '@/components/providers/config-provider';
import {
  isParentUrlChangedMessage,
  postSuggestionContext,
  type ParentUrlChangedMessage,
} from '@/lib/constants/post-message-types';
import { isFromEmbeddingPage } from '@/lib/utils/dom';

// The strip is the only reader of a refresh, so it decides when one is worth a request: a URL
// change that arrives mid-conversation is held until the welcome screen is back, unless a CTA
// is waiting on the answer (`needsResolved`).
export function useParentUrlSuggestions({ suggestionsVisible }: { suggestionsVisible: boolean }) {
  const locale = useLocale();
  const setTranslations = useSetTranslations();
  const [pending, setPending] = useState(false);
  const held = useRef<ParentUrlChangedMessage | null>(null);
  const token = useRef(0);

  const run = useCallback(
    (message: ParentUrlChangedMessage): void => {
      const currentToken = ++token.current;
      setPending(true);
      void import('@/lib/dynamic-suggestions/apply-suggestion-context')
        .then(async ({ applySuggestionContext }) => {
          const resolvedContext = await applySuggestionContext({
            parentUrl: message.parentUrl,
            domContext: message.domContext,
            locale,
            isStale: () => currentToken !== token.current,
            setTranslations,
          });
          if (currentToken !== token.current) return;
          if (message.needsResolved) postSuggestionContext({ resolvedContext });
        })
        // Request and chunk-load failures share the same outcome: ordinary work stays held, while
        // a CTA receives an answer so it cannot remain blocked.
        .catch(() => {
          if (currentToken !== token.current) return;
          if (message.needsResolved) {
            postSuggestionContext({ resolvedContext: null, failed: true });
          } else {
            held.current = message;
            postSuggestionContext({ resolvedContext: null, failed: true, retry: true });
          }
        })
        .finally(() => {
          if (currentToken === token.current) setPending(false);
        });
    },
    [locale, setTranslations],
  );

  useEvent('message', (event: MessageEvent) => {
    if (!isFromEmbeddingPage(event)) return;
    if (!isParentUrlChangedMessage(event.data)) return;
    if (suggestionsVisible || event.data.needsResolved) {
      held.current = null;
      run(event.data);
    } else {
      token.current += 1;
      held.current = event.data;
    }
  });

  useEffect(() => {
    if (suggestionsVisible && held.current) {
      const message = held.current;
      held.current = null;
      run(message);
    }
  }, [run, suggestionsVisible]);

  return { pending };
}
